import { useState, useEffect, useCallback } from 'react'
import { openStoredPdf } from '@/lib/openPdf'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { DevicePhoneMobileIcon, CreditCardIcon, BanknotesIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useInvoice } from '@/hooks/useInvoices'
import { useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { invoiceStatusI18nKey, invoiceStatusBadgeClass } from '@/lib/invoiceStatus'

// Cache stripe promises by key to avoid re-initializing
const stripeCache = new Map<string, ReturnType<typeof loadStripe>>()
function getStripe(publishableKey: string) {
  if (!publishableKey) return null
  if (!stripeCache.has(publishableKey)) {
    stripeCache.set(publishableKey, loadStripe(publishableKey))
  }
  return stripeCache.get(publishableKey)!
}

function getStripeKeyFromPaymentMethods(paymentMethods: Record<string, unknown> | undefined): string {
  if (!paymentMethods) return ''
  const promptpay = paymentMethods.promptpay as Record<string, unknown> | undefined
  const card = paymentMethods.credit_card as Record<string, unknown> | undefined
  return (promptpay?.stripe_publishable_key as string) || (card?.stripe_publishable_key as string) || ''
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2 }).format(amount)
}

// PromptPay QR payment component
function PromptPayPayment({
  invoiceId,
  amount,
  stripeKey,
  email,
  onSuccess,
  onError,
}: {
  invoiceId: number
  amount: number
  stripeKey: string
  email: string
  onSuccess: () => void
  onError: (msg: string) => void
}) {
  const { t } = useTranslation('invoices')
  const { t: tc } = useTranslation('common')
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [paymentId, setPaymentId] = useState<number | null>(null)
  const [, setIsLoading] = useState(false)
  const [isPolling, setIsPolling] = useState(false)
  const queryClient = useQueryClient()

  const initiate = async () => {
    setIsLoading(true)
    try {
      const response = await api.post<{
        client_secret: string
        payment_id: number
        charge_amount?: number
        qr_code_url?: string
      }>('/api/customer/payments', {
        invoice_id: invoiceId,
        payment_method: 'promptpay',
      })
      const { client_secret, payment_id, charge_amount, qr_code_url } = response.data

      if (charge_amount && charge_amount !== amount) {
        /* server is the source of truth for the charged amount; UI amount is display-only */
      }

      const stripe = await getStripe(stripeKey)
      if (!stripe) {
        onError(t('errors.stripe_not_configured'))
        return
      }

      const result = await stripe.confirmPromptPayPayment(client_secret, {
        payment_method: {
          billing_details: { email },
        },
      })
      if (result.error) {
        onError(result.error.message ?? t('payment_failed'))
        return
      }

      // If payment already succeeded (e.g. instant confirmation)
      if (result.paymentIntent?.status === 'succeeded') {
        setPaymentId(payment_id)
        onSuccess()
        return
      }

      const nextAction = result.paymentIntent?.next_action as Record<string, unknown> | null | undefined
      const promptpayData = nextAction?.['promptpay_display_qr_code'] as Record<string, unknown> | undefined
      const clientQrUrl = promptpayData?.['image_url_svg'] as string | undefined
      // APP-C2: server-side confirm returns QR in response body; client-side
      // confirm may also include it in next_action. Use whichever is available.
      const finalQrUrl = clientQrUrl || qr_code_url
      if (finalQrUrl) {
        setQrUrl(finalQrUrl)
        setPaymentId(payment_id)
        setIsPolling(true)
      } else {
        // QR not available — start polling anyway in case webhook confirms it
        setPaymentId(payment_id)
        setIsPolling(true)
      }
    } catch {
      onError(tc('errors.something_went_wrong'))
    } finally {
      setIsLoading(false)
    }
  }

  // Auto-initiate PromptPay on mount — no confirm button needed
  useEffect(() => {
    void initiate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Poll invoice — webhook confirms payment and updates amount_paid
  useEffect(() => {
    if (!isPolling || !paymentId) return

    let attempts = 0
    const maxAttempts = 120 // 10 minutes at 5s intervals

    const poll = setInterval(async () => {
      attempts++
      if (attempts > maxAttempts) {
        clearInterval(poll)
        setIsPolling(false)
        return
      }
      try {
        const response = await api.get<{
          data: { status: string; payments: { id: number; confirmed_at?: string | null }[] }
        }>(`/api/customer/invoices/${invoiceId}`)
        const invoice = response.data.data
        // Check if our payment was confirmed OR invoice is fully paid
        const confirmed = invoice.payments?.some(
          (p) => p.id === paymentId && p.confirmed_at
        )
        if (invoice.status === 'paid' || confirmed) {
          clearInterval(poll)
          void queryClient.invalidateQueries({ queryKey: ['customer', 'invoices'] })
          onSuccess()
        }
      } catch {
        // Ignore poll errors
      }
    }, 5000)

    return () => clearInterval(poll)
  }, [isPolling, paymentId, invoiceId, onSuccess, queryClient])

  if (qrUrl) {
    return (
      <div className="text-center space-y-4">
        <p className="font-medium">{t('scan_qr')}</p>
        <img src={qrUrl} alt="PromptPay QR" className="mx-auto w-48 h-48" />
        <p className="text-sm text-base-content/60">{t('polling_message')}</p>
        <span className="loading loading-spinner loading-sm text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-4 text-center">
      <p className="text-base-content/70 text-sm">
        {t('amount_via_promptpay', { amount: formatCurrency(amount) })}
      </p>
      <span className="loading loading-spinner loading-md text-primary" />
      <p className="text-sm text-base-content/60">
        {isPolling ? t('polling_message') : t('generating_qr')}
      </p>
    </div>
  )
}

// Card payment form
function CardPaymentForm({
  invoiceId,
  amount,
  onSuccess,
  onError,
}: {
  invoiceId: number
  amount: number
  onSuccess: () => void
  onError: (msg: string) => void
}) {
  const { t } = useTranslation('invoices')
  const { t: tc } = useTranslation('common')
  const stripe = useStripe()
  const elements = useElements()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return

    setIsLoading(true)
    try {
      const response = await api.post<{ client_secret: string; charge_amount?: number; payment_id: number }>('/api/customer/payments', {
        invoice_id: invoiceId,
        payment_method: 'card',
      })
      const { client_secret, charge_amount } = response.data
      if (charge_amount && charge_amount !== amount) {
        /* server is the source of truth for the charged amount; UI amount is display-only */
      }

      const cardElement = elements.getElement(CardElement)
      if (!cardElement) return

      const result = await stripe.confirmCardPayment(client_secret, {
        payment_method: { card: cardElement },
      })

      if (result.error) {
        onError(result.error.message ?? t('payment_failed'))
      } else {
        // Stripe webhook will confirm the payment — poll invoice briefly
        for (let i = 0; i < 10; i++) {
          await new Promise(r => setTimeout(r, 2000))
          try {
            const res = await api.get<{ data: { status: string } }>(`/api/customer/invoices/${invoiceId}`)
            if (res.data.data.status === 'paid') break
          } catch { /* ignore */ }
        }
        void queryClient.invalidateQueries({ queryKey: ['customer', 'invoices'] })
        onSuccess()
      }
    } catch {
      onError(tc('errors.something_went_wrong'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={(e) => { void handleSubmit(e) }} className="space-y-4">
      <p className="text-sm font-medium">{t('card_details')}</p>
      <div className="border border-base-300 rounded-lg p-3">
        <CardElement options={{ style: { base: { fontSize: '16px' } } }} />
      </div>
      <p className="text-base-content/70 text-sm">฿{formatCurrency(amount)}</p>
      <button type="submit" className="btn btn-primary w-full" disabled={!stripe || isLoading}>
        {isLoading ? <span className="loading loading-spinner loading-sm" /> : t('confirm_payment')}
      </button>
    </form>
  )
}

// Bank transfer with slip upload
function BankTransferPayment({
  invoiceId,
  amount,
  bankTransfer,
  onSuccess,
  onError,
}: {
  invoiceId: number
  amount: number
  bankTransfer: Record<string, unknown> | undefined
  onSuccess: () => void
  onError: (msg: string) => void
}) {
  const { t } = useTranslation('invoices')
  const { t: tc } = useTranslation('common')
  const [slipPreview, setSlipPreview] = useState<string | null>(null)
  const [slipFile, setSlipFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setSlipFile(file)
    const reader = new FileReader()
    reader.onload = () => setSlipPreview(reader.result as string)
    reader.readAsDataURL(file)
  }

  const handleSubmit = async () => {
    if (!slipFile) return
    setIsUploading(true)
    try {
      // Get presigned URL
      const presignedRes = await api.post<{ url: string; key: string }>('/api/customer/presigned_urls', {
        content_type: slipFile.type,
      })
      const { url: uploadUrl, key } = presignedRes.data

      // Upload file to S3
      await fetch(uploadUrl, {
        method: 'PUT',
        body: slipFile,
        headers: { 'Content-Type': slipFile.type },
      })

      // LIFF-M2: store the BARE storage key (project convention) — not the
      // presigned URL host/path.
      await api.post('/api/customer/payments', {
        invoice_id: invoiceId,
        payment_method: 'transfer',
        slip_url: key,
      })

      setSubmitted(true)
      onSuccess()
    } catch {
      onError(tc('errors.something_went_wrong'))
    } finally {
      setIsUploading(false)
    }
  }

  if (submitted) {
    return (
      <div className="text-center space-y-4 py-4">
        <div className="text-4xl">✓</div>
        <p className="font-medium">{t('slip_submitted')}</p>
        <p className="text-sm text-base-content/60">{t('pending_verification')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-sm font-medium">{t('bank_transfer_details')}</p>
      <div className="bg-base-200 rounded-lg p-4 space-y-2 text-sm">
        {Boolean(bankTransfer?.bank_name) && (
          <div className="flex justify-between">
            <span className="text-base-content/60">{t('bank_name')}</span>
            <span className="font-medium">{String(bankTransfer?.bank_name)}</span>
          </div>
        )}
        {Boolean(bankTransfer?.account_name) && (
          <div className="flex justify-between">
            <span className="text-base-content/60">{t('account_name')}</span>
            <span className="font-medium">{String(bankTransfer?.account_name)}</span>
          </div>
        )}
        {Boolean(bankTransfer?.account_number) && (
          <div className="flex justify-between">
            <span className="text-base-content/60">{t('account_number')}</span>
            <span className="font-medium font-mono">{String(bankTransfer?.account_number)}</span>
          </div>
        )}
      </div>
      <p className="text-base-content/70 text-sm">
        {t('transfer_amount')}: ฿{formatCurrency(amount)}
      </p>

      <div className="divider my-1" />
      <p className="text-sm font-medium">{t('upload_slip')}</p>

      {slipPreview ? (
        <div className="relative">
          <img src={slipPreview} alt="Slip" className="w-full rounded-lg max-h-64 object-contain bg-base-200" />
          <button
            className="btn btn-circle btn-sm btn-ghost absolute top-2 right-2"
            onClick={() => { setSlipPreview(null); setSlipFile(null) }}
          >✕</button>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-base-300 rounded-lg cursor-pointer hover:border-primary">
          <span className="text-sm text-base-content/60">{t('tap_to_upload')}</span>
          <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
        </label>
      )}

      <button
        className="btn btn-primary w-full"
        disabled={!slipFile || isUploading}
        onClick={() => { void handleSubmit() }}
      >
        {isUploading ? <span className="loading loading-spinner loading-sm" /> : t('submit_slip')}
      </button>
    </div>
  )
}

// Payment modal
function PaymentModal({
  invoiceId,
  remaining,
  stripeKey,
  paymentMethods,
  onClose,
  onSuccess,
}: {
  invoiceId: number
  remaining: number
  stripeKey: string
  paymentMethods: Record<string, unknown>
  onClose: () => void
  onSuccess: () => void
}) {
  const { t } = useTranslation('invoices')
  const { customer } = useAuth()
  const [method, setMethod] = useState<'promptpay' | 'card' | 'bank_transfer' | null>(null)
  const [paymentError, setPaymentError] = useState<string | null>(null)

  const promptpayEnabled = (paymentMethods?.promptpay as Record<string, unknown>)?.enabled === true
  const cardEnabled = (paymentMethods?.credit_card as Record<string, unknown>)?.enabled === true
  const bankTransferEnabled = (paymentMethods?.bank_transfer as Record<string, unknown>)?.enabled === true
  const bankTransfer = paymentMethods?.bank_transfer as Record<string, unknown> | undefined

  const handleSuccess = useCallback(() => {
    onSuccess()
    onClose()
  }, [onSuccess, onClose])

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50 pointer-events-auto" onClick={onClose}>
      <div className="bg-base-100 w-full rounded-t-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">{t('payment_method')}</h3>
          <button className="btn btn-ghost btn-sm btn-circle" onClick={onClose}>✕</button>
        </div>

        {paymentError && (
          <div className="alert alert-error text-sm mb-4">
            <span>{paymentError}</span>
          </div>
        )}

        {!method ? (
          <div className="space-y-3">
            {promptpayEnabled && (
              <button
                className="btn btn-outline w-full justify-start gap-3"
                onClick={() => setMethod('promptpay')}
              >
                <DevicePhoneMobileIcon className="w-5 h-5" />
                {t('promptpay')}
              </button>
            )}
            {cardEnabled && (
              <button
                className="btn btn-outline w-full justify-start gap-3"
                onClick={() => setMethod('card')}
              >
                <CreditCardIcon className="w-5 h-5" />
                {t('credit_card')}
              </button>
            )}
            {bankTransferEnabled && (
              <button
                className="btn btn-outline w-full justify-start gap-3"
                onClick={() => setMethod('bank_transfer')}
              >
                <BanknotesIcon className="w-5 h-5" />
                {t('bank_transfer')}
              </button>
            )}
            {!promptpayEnabled && !cardEnabled && !bankTransferEnabled && (
              <p className="text-base-content/60 text-sm text-center py-4">{t('no_payment_methods')}</p>
            )}
          </div>
        ) : method === 'bank_transfer' ? (
          <BankTransferPayment
            invoiceId={invoiceId}
            amount={remaining}
            bankTransfer={bankTransfer}
            onSuccess={handleSuccess}
            onError={setPaymentError}
          />
        ) : method === 'promptpay' ? (
          <PromptPayPayment
            invoiceId={invoiceId}
            amount={remaining}
            stripeKey={stripeKey}
            email={customer?.email || `customer-${customer?.id}@flowrent.io`}
            onSuccess={handleSuccess}
            onError={setPaymentError}
          />
        ) : getStripe(stripeKey) ? (
          <Elements stripe={getStripe(stripeKey)!}>
            <CardPaymentForm
              invoiceId={invoiceId}
              amount={remaining}
              onSuccess={handleSuccess}
              onError={setPaymentError}
            />
          </Elements>
        ) : (
          <p className="text-error text-sm">{t('errors.stripe_not_configured')}</p>
        )}
      </div>
    </div>
  )
}

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation('invoices')
  const { t: tc } = useTranslation('common')
  const { formatDate } = useDateFormatter()
  const queryClient = useQueryClient()
  const { data: invoice, isLoading, error } = useInvoice(Number(id))
  const [showPayment, setShowPayment] = useState(false)
  const [paymentSuccess, setPaymentSuccess] = useState(false)

  const handlePaymentSuccess = useCallback(() => {
    setPaymentSuccess(true)
    void queryClient.invalidateQueries({ queryKey: ['customer', 'invoices'] })
    void queryClient.invalidateQueries({ queryKey: ['customer', 'invoices', Number(id)] })
  }, [queryClient, id])

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error || !invoice) {
    return (
      <div className="p-4">
        <div className="alert alert-error">{tc('errors.something_went_wrong')}</div>
      </div>
    )
  }

  // LIFF-H6: the payable amount comes from the server — no client money math
  const remaining = Number(invoice.balance_due)
  const canPay = invoice.status !== 'paid'

  return (
    <div className="p-4 space-y-4 pb-24">
      {paymentSuccess && (
        <div className="alert alert-success">
          <span>{t('payment_success')}</span>
        </div>
      )}

      {/* Header */}
      <div className="card bg-primary text-primary-content">
        <div className="card-body p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm opacity-80">{t('invoice_number', { number: invoice.number })}</p>
              <p className="text-3xl font-bold mt-1">฿{formatCurrency(invoice.total)}</p>
            </div>
            {/* The badge tints are made for a white card. On this dark header
                they cannot be read, so the badge gets a white pill here. */}
            <span className={`${invoiceStatusBadgeClass(invoice.status)} !bg-base-100`}>
              {t(invoiceStatusI18nKey(invoice.status))}
            </span>
          </div>
          <div className="flex gap-4 text-sm mt-2 opacity-80">
            <span>{t('billed_at')}: {formatDate(invoice.billed_at)}</span>
            <span>{t('due_at')}: {formatDate(invoice.due_at)}</span>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="flat-card">
        <div className="card-body p-4">
          <h3 className="font-semibold mb-3">{t('items')}</h3>
          <div className="space-y-2">
            {invoice.items.map((item) => (
              <div key={item.id} className="flex justify-between text-sm">
                <span className="text-base-content/80">{item.description}</span>
                <span className="font-medium">฿{formatCurrency(item.amount)}</span>
              </div>
            ))}
          </div>
          <div className="divider my-2" />
          <div className="flex justify-between font-bold">
            <span>{t('total')}</span>
            <span>฿{formatCurrency(invoice.total)}</span>
          </div>
          {invoice.amount_paid > 0 && (
            <>
              <div className="flex justify-between text-sm text-success">
                <span>{t('amount_paid')}</span>
                <span>฿{formatCurrency(invoice.amount_paid)}</span>
              </div>
              <div className="flex justify-between text-sm font-semibold text-warning">
                <span>{t('remaining')}</span>
                <span>฿{formatCurrency(remaining)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Download Invoice PDF */}
      {invoice.pdf_key && (
        <div className="flat-card">
          <div className="card-body p-4">
            <button
              className="btn btn-outline btn-primary w-full gap-2"
              onClick={() => void openStoredPdf(invoice.pdf_key!, `invoice-${invoice.number || invoice.id}.pdf`)}
            >
              <DocumentArrowDownIcon className="w-5 h-5" />
              {t('download_pdf')}
            </button>
          </div>
        </div>
      )}

      {/* Payment history */}
      {invoice.payments && invoice.payments.length > 0 && (
        <div className="flat-card">
          <div className="card-body p-4">
            <h3 className="font-semibold mb-3">{t('payments')}</h3>
            <div className="space-y-2">
              {invoice.payments.map((payment) => (
                <div key={payment.id} className="flex justify-between text-sm">
                  <div>
                    <p className="font-medium">{formatDate(payment.paid_at)}</p>
                    <p className="text-xs text-base-content/60">{payment.payment_method}</p>
                  </div>
                  <span className="text-success font-medium">฿{formatCurrency(payment.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pay button */}
      {canPay && !paymentSuccess && (
        <div className="fixed bottom-[calc(56px+env(safe-area-inset-bottom))] left-0 right-0 px-4">
          <div className="max-w-[430px] mx-auto">
            <button
              className="btn btn-primary w-full shadow-lg"
              onClick={() => setShowPayment(true)}
            >
              {t('pay_button', { amount: formatCurrency(remaining) })}
            </button>
          </div>
        </div>
      )}

      {showPayment && (
        <PaymentModal
          invoiceId={invoice.id}
          remaining={remaining}
          stripeKey={getStripeKeyFromPaymentMethods(invoice.property?.payment_methods)}
          paymentMethods={invoice.property?.payment_methods ?? {}}
          onClose={() => setShowPayment(false)}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  )
}
